import { binaryToUtf8 } from '@streamr/utils';
import { ContentType, EncryptionType } from '@streamr/trackerless-network';
import { StreamrClientError } from '../StreamrClientError';
import { MessageRef } from './MessageRef';
import { ValidationError } from './ValidationError';
import { validateIsDefined } from './validations';
export var StreamMessageType;
(function (StreamMessageType) {
    StreamMessageType[StreamMessageType["MESSAGE"] = 0] = "MESSAGE";
    StreamMessageType[StreamMessageType["GROUP_KEY_REQUEST"] = 1] = "GROUP_KEY_REQUEST";
    StreamMessageType[StreamMessageType["GROUP_KEY_RESPONSE"] = 2] = "GROUP_KEY_RESPONSE";
})(StreamMessageType || (StreamMessageType = {}));
/**
 * Validates that messageId is strictly after prevMsgRef in time.
 */
function validateSequence(messageId, prevMsgRef) {
    if (prevMsgRef === undefined) {
        return;
    }
    const comparison = messageId.toMessageRef().compareTo(prevMsgRef);
    if (comparison === 0) {
        throw new ValidationError(
        // eslint-disable-next-line max-len
        `prevMessageRef cannot be identical to current. Current: ${JSON.stringify(messageId.toMessageRef())} Previous: ${JSON.stringify(prevMsgRef)}`);
    }
    if (comparison < 0) {
        throw new ValidationError(`prevMessageRef must come before current. Current: ${JSON.stringify(messageId.toMessageRef())} Previous: ${JSON.stringify(prevMsgRef)}`);
    }
}
/**
 * An internal class representing a message in a stream. Applications see instances of the Message class.
 */
export class StreamMessage {
    messageId;
    prevMsgRef;
    messageType;
    content;
    contentType;
    signature;
    signatureType;
    encryptionType;
    groupKeyId;
    newGroupKey;
    constructor({ messageId, prevMsgRef, messageType = StreamMessageType.MESSAGE, content, contentType, signature, signatureType, encryptionType, groupKeyId, newGroupKey, }) {
        validateSequence(messageId, prevMsgRef);
        if (encryptionType === EncryptionType.AES) {
            validateIsDefined('groupKeyId', groupKeyId);
        }
        this.messageId = messageId;
        this.prevMsgRef = prevMsgRef;
        this.messageType = messageType;
        this.contentType = contentType;
        this.encryptionType = encryptionType;
        this.groupKeyId = groupKeyId;
        this.newGroupKey = newGroupKey;
        this.signature = signature;
        this.signatureType = signatureType;
        this.content = content;
    }
    getStreamId() {
        return this.messageId.streamId;
    }
    getStreamPartition() {
        return this.messageId.streamPartition;
    }
    getStreamPartID() {
        return this.messageId.getStreamPartID();
    }
    getTimestamp() {
        return this.messageId.timestamp;
    }
    getSequenceNumber() {
        return this.messageId.sequenceNumber;
    }
    getPublisherId() {
        return this.messageId.publisherId;
    }
    getMsgChainId() {
        return this.messageId.msgChainId;
    }
    getMessageRef() {
        return new MessageRef(this.getTimestamp(), this.getSequenceNumber());
    }
    // TODO: consider replacing later half of type with a "JSON type" from a ts-toolbelt or type-fest or ts-essentials
    getParsedContent() {
        if (this.encryptionType !== EncryptionType.NONE || this.contentType === ContentType.BINARY) {
            return this.content;
        }
        else if (this.contentType === ContentType.JSON) {
            try {
                return JSON.parse(binaryToUtf8(this.content));
            }
            catch (err) {
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                throw new StreamrClientError(`Unable to parse JSON: ${err}`, 'INVALID_MESSAGE_CONTENT', this);
            }
        }
        else {
            throw new StreamrClientError(`Unknown content type: ${this.contentType}`, 'ASSERTION_FAILED', this);
        }
    }
    static isAESEncrypted(msg) {
        return msg.encryptionType === EncryptionType.AES;
    }
}
//# sourceMappingURL=StreamMessage.js.map